import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Timesheet } from '@cloudsign/shared';
import type { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useSite } from '../../lib/site';
import { ExportButton } from '../../components/ExportButton';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Alert, Avatar, Button, EmptyState, FilterBar, PageHeader, SelectField, Spinner } from '../../components/ui';
import { DateRangePicker, isRangeValid, minutesToTime } from '../../components/phase2';
import type { DateRange } from '../../components/phase2';
import { EditEntryDialog, AddEntryDialog } from './EntryDialog';
import type { EditTarget } from './EntryDialog';
import { Axis, DayBar, Legend } from './TimesheetBar';
import type { Segment } from './TimesheetBar';
import { formatDuration, reportsKey, useActiveGroups, useTenantZone } from './api';
import { addDays, dayLabel, daysBetween, todayIn } from '../../lib/zoneTime';

type Sheet = z.infer<typeof Timesheet>;
type Person = Sheet['people'][number];

const MAX_DAYS = 31;

/** The whole record behind a segment, joined across midnight, in the sheet's zone. A record cut off by the range keeps the part we can see. */
function targetFor(person: Person, seg: Segment): EditTarget {
  const days = [...person.days].sort((a, b) => a.date.localeCompare(b.date));
  const parts = days.flatMap((d) => d.segments.filter((s) => s.visitId === seg.visitId).map((s) => ({ date: d.date, s })));
  const first = parts[0] ?? { date: days[0]?.date ?? '', s: seg };
  const last = parts[parts.length - 1] ?? first;
  const endsNextDay = last.s.endMinute >= 1440;
  return {
    visitId: seg.visitId,
    personName: person.name,
    siteName: seg.siteName,
    signedIn: { date: first.date, time: minutesToTime(first.s.startMinute) },
    signedOut: last.s.open ? null : { date: endsNextDay ? addDays(last.date, 1) : last.date, time: minutesToTime(last.s.endMinute) },
  };
}

function PersonCard({ person, index, onPick }: { person: Person; index: number; onPick: (p: Person, s: Segment) => void }) {
  const days = [...person.days].sort((a, b) => a.date.localeCompare(b.date));
  return (
    <section aria-labelledby={`ts-${index}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 id={`ts-${index}`} className="flex items-center gap-3 text-base font-semibold text-slate-900">
          <Avatar name={person.name} />
          {person.name}
        </h2>
        <p className="text-sm text-slate-700">{t('rpt.ts.total', { total: formatDuration(person.totalMinutes) })}</p>
      </div>
      {days.length === 0 ? (
        <p className="text-sm text-slate-600">{t('rpt.ts.noRecords')}</p>
      ) : (
        <div className="space-y-2">
          <div className="hidden gap-3 sm:grid sm:grid-cols-[7rem_1fr_6rem]">
            <span />
            <Axis />
            <span />
          </div>
          {days.map((d) => (
            <div key={d.date} className="grid items-center gap-x-3 gap-y-1 sm:grid-cols-[7rem_1fr_6rem]">
              <span className="text-sm font-medium text-slate-800">{dayLabel(d.date)}</span>
              <DayBar segments={d.segments} onPick={(s) => onPick(person, s)} />
              <span className="text-sm text-slate-700 sm:text-right">{formatDuration(d.minutes)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Reports > Timesheets: a group's sign-ins per person per day as an hour-by-hour bar, with totals and audited edits. */
export function TimesheetPage() {
  const canExport = useCan('timesheets.export');
  const canEdit = useCan('timesheets.edit');
  const canDelete = useCan('history.delete') && canEdit;
  const zone = useTenantZone();
  const { sites, siteId: currentSite } = useSite();
  const groups = useActiveGroups();
  const [groupChoice, setGroupChoice] = useState('');
  const [siteId, setSiteId] = useState('');
  const [range, setRange] = useState<DateRange>(() => {
    const today = todayIn(zone);
    return { from: addDays(today, -6), to: today };
  });
  const [editing, setEditing] = useState<{ person: Person; target: EditTarget } | null>(null);
  const [adding, setAdding] = useState(false);

  const groupList = groups.data ?? [];
  const defaultGroup = (groupList.find((g) => g.kind === 'repeat') ?? groupList[0])?.id ?? '';
  const groupId = groupChoice || defaultGroup;
  const complete = range.from !== '' && range.to !== '' && isRangeValid(range);
  const tooLong = complete && daysBetween(range.from, range.to) + 1 > MAX_DAYS;
  const ready = complete && !tooLong && groupId !== '';
  const filters = { groupId, siteId: siteId || undefined, from: range.from, to: range.to };
  const q = useQuery({
    queryKey: [...reportsKey, 'timesheet', filters],
    enabled: ready,
    queryFn: () => api.get('/reports/timesheet', { query: filters, schema: Timesheet }),
  });
  const sheetZone = q.data?.timeZone ?? zone;
  const people = q.data?.people ?? [];
  const everyone = people.reduce((n, p) => n + p.totalMinutes, 0);

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <PageHeader
        title={t('rpt.ts.title')}
        description={t('rpt.ts.description')}
        actions={
          <>
            {canEdit ? (
              <Button variant="primary" disabled={groupId === ''} onClick={() => setAdding(true)}>
                {t('rpt.ts.addRecord')}
              </Button>
            ) : null}
            {canExport ? <ExportButton path="/reports/timesheet/export" query={filters} fileName="timesheet" disabled={!ready} /> : null}
          </>
        }
      />

      <FilterBar columns="sm:grid-cols-2">
        <SelectField label={t('rpt.filter.group')} value={groupId} onChange={(e) => setGroupChoice(e.target.value)}>
          {groupList.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('rpt.filter.site')} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">{t('rpt.filter.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <div className="sm:col-span-2">
          <DateRangePicker label={t('rpt.filter.dates')} value={range} today={new Date(`${todayIn(zone)}T12:00:00`)} onChange={setRange} />
        </div>
      </FilterBar>

      {!complete ? <Alert kind="info">{t('rpt.range.needBoth')}</Alert> : null}
      {tooLong ? <Alert>{t('rpt.ts.tooLong', { days: MAX_DAYS })}</Alert> : null}

      {!ready ? null : q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-700" aria-live="polite">
              {t('rpt.ts.summary', { people: people.length, total: formatDuration(everyone), zone: sheetZone })}
            </p>
            <Legend />
          </div>
          {people.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <EmptyState message={t('rpt.ts.empty')} />
            </div>
          ) : (
            <div className="space-y-3">
              {people.map((p, i) => (
                <PersonCard key={p.memberId ?? p.name} index={i} person={p} onPick={(person, seg) => setEditing({ person, target: targetFor(person, seg) })} />
              ))}
            </div>
          )}
        </>
      )}

      {editing ? <EditEntryDialog target={editing.target} zone={sheetZone} canEdit={canEdit} canDelete={canDelete} onClose={() => setEditing(null)} /> : null}
      {adding ? <AddEntryDialog groupId={groupId} defaultSiteId={siteId || currentSite || ''} zone={sheetZone} onClose={() => setAdding(false)} /> : null}
    </div>
  );
}
